import {
  CommitScopeChoice,
  type ConversationCommitFiles,
  useCommitScope,
} from '@crowdex/host-kit/renderer/commit-scope';
import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import { Dialog, DialogContent, DialogTitle } from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { Textarea } from '@crowdex/ui/components/textarea';
import { toast } from '@crowdex/ui/components/toaster';
import { createPullRequestWithOptionalBranch } from '@crowdex/workspace-protocol/git-action-workflows';
import {
  buildCommitPrompt,
  buildPullRequestPrompt,
  COMMIT_METADATA_JSON_SCHEMA,
  commitMetadataSchema,
  PULL_REQUEST_METADATA_JSON_SCHEMA,
  pullRequestMetadataSchema,
} from '@crowdex/workspace-protocol/git-generation';
import { GitBranch, GitPullRequest, RefreshCw, Sparkles } from 'lucide-react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { recordConversationPullRequest } from '@/lib/desktop-shell/use-pull-request-sync';
import { errorMessage } from '@/lib/error-message';
import {
  commitGitChanges,
  createGitBranch,
  createPullRequest,
  pushGitBranch,
  readGitGenerationContext,
} from './pull-request-client';

export type CreatePullRequestInput = {
  /** The session's checkout. */
  workspace: string;
  branch: string;
  branches: string[];
  hasChanges: boolean;
  draft: boolean;
  /**
   * The files the open conversation's turns changed: the local changes committed with the pull
   * request are those, unless the user picks the whole folder.
   */
  conversationFiles?: ConversationCommitFiles | null;
};
export type CreatePullRequestResult = { pullRequestUrl: string | null };

/** Structured generation with the session's model (commit and pull request text). */
export type PullRequestTextGenerator = (
  prompt: string,
  schema: Readonly<Record<string, unknown>>,
) => Promise<unknown>;

/**
 * Publishes the session's branch as a pull request: git runs in the session's checkout, the
 * title, description and commit message come from the session's model.
 */
export function CreatePullRequestDialog({
  input,
  generateText,
  onRefresh,
  onClose,
}: {
  input: CreatePullRequestInput;
  generateText: PullRequestTextGenerator | undefined;
  onRefresh?: (() => Promise<void>) | undefined;
  onClose: (result: CreatePullRequestResult | null) => void;
}) {
  const shell = useOptionalDesktopShellStoreApi();
  // The conversation it is opened from: the pull request is that conversation's.
  const [conversationId] = useState(() => shell?.getState().chatId ?? null);
  const [branchName, setBranchName] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [commitMessage, setCommitMessage] = useState('');
  const [draft, setDraft] = useState(input.draft);
  const [includeLocalChanges, setIncludeLocalChanges] = useState(true);
  const [generating, setGenerating] = useState({ commit: false, pr: false });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Completed steps survive a retry, so a failed push does not commit twice.
  const progress = useRef({ branch: null as string | null, committed: false, pushed: false });
  const fields = useRef({ title: '', body: '', commitMessage: '' });
  fields.current = { title, body, commitMessage };

  const branch = input.branch.trim() || progress.current.branch;
  const hasBranch = Boolean(branch);
  const hasChanges = input.hasChanges;
  const branchExists = input.branches.includes(branchName.trim());
  const commitScope = useCommitScope({
    workspace: input.workspace,
    conversationFiles: input.conversationFiles,
    active: hasChanges && includeLocalChanges,
    read: readGitGenerationContext,
  });
  const nothingInScope = commitScope.preview !== null && commitScope.preview.files.length === 0;

  async function generate(kind: 'commit' | 'pr', overwrite = false) {
    setGenerating((current) => ({ ...current, [kind]: true }));
    setError(null);
    try {
      if (!generateText) throw new Error('Generation is unavailable here.');
      const git = await readGitGenerationContext(input.workspace, commitScope.paths);
      if (kind === 'commit') {
        const generated = commitMetadataSchema.parse(
          await generateText(buildCommitPrompt(git), COMMIT_METADATA_JSON_SCHEMA),
        );
        if (overwrite || !fields.current.commitMessage.trim())
          setCommitMessage(generated.message.trim());
      } else {
        const generated = pullRequestMetadataSchema.parse(
          await generateText(
            buildPullRequestPrompt({
              branch: branch ?? null,
              commitMessage: fields.current.commitMessage,
              context: git,
            }),
            PULL_REQUEST_METADATA_JSON_SCHEMA,
          ),
        );
        if (overwrite || !fields.current.title.trim()) setTitle(generated.title.trim());
        if (overwrite || !fields.current.body.trim()) setBody(generated.body.trim());
      }
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setGenerating((current) => ({ ...current, [kind]: false }));
    }
  }

  // The commit message says what the commit takes: written again when its scope changes.
  const scopeRef = useRef(commitScope.scope);
  const regenerateCommit = useEffectEvent(() => {
    if (hasChanges) void generate('commit', true);
  });
  useEffect(() => {
    if (scopeRef.current === commitScope.scope) return;
    scopeRef.current = commitScope.scope;
    regenerateCommit();
  }, [commitScope.scope]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (input.hasChanges) void generate('commit');
    void generate('pr');
    // Generation starts once, when the dialog opens.
  });

  async function create() {
    setWorking(true);
    setError(null);
    try {
      const result = await createPullRequestWithOptionalBranch(
        {
          workspace: input.workspace,
          currentBranch: branch ?? null,
          newBranch: branchName,
          // Nothing of the conversation's left to commit: the pull request takes the branch as is.
          hasChanges: hasChanges && !nothingInScope,
          includeLocalChanges,
          commitMessage,
          title,
          body,
          draft,
          commitCompleted: progress.current.committed,
          pushCompleted: progress.current.pushed,
        },
        {
          createBranch: (workspace, name) => createGitBranch(workspace, name),
          onBranchCreated: (name) => {
            progress.current.branch = name;
          },
          commit: ({ workspaceRoot, message }) =>
            commitGitChanges(workspaceRoot, message, commitScope.paths),
          onCommitted: () => {
            progress.current.committed = true;
          },
          push: (workspace) => pushGitBranch(workspace),
          onPushed: () => {
            progress.current.pushed = true;
          },
          createPullRequest: ({ workspaceRoot, title: prTitle, body: prBody, draft: prDraft }) =>
            createPullRequest(workspaceRoot, { title: prTitle, body: prBody, draft: prDraft }),
        },
      );
      toast.success(draft ? 'Draft pull request published' : 'Pull request published');
      await onRefresh?.().catch(() => undefined);
      if (shell !== null && conversationId !== null)
        await recordConversationPullRequest(
          shell,
          conversationId,
          input.workspace,
          result.pullRequestUrl,
        );
      onClose({ pullRequestUrl: result.pullRequestUrl });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setWorking(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !working && onClose(null)}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto p-0 sm:max-w-lg">
        <div className="flex flex-col">
          <header className="flex items-start gap-3.5 border-hairline border-b bg-muted/25 p-6 pr-14">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-hairline-strong bg-background shadow-sm">
              <GitPullRequest className="size-5 text-foreground/80" />
            </div>
            <div className="min-w-0 pt-0.5">
              <DialogTitle>Create pull request</DialogTitle>
              <p className="mt-1 text-muted-foreground text-sm">
                Review the generated details before publishing your branch.
              </p>
            </div>
            <Button
              className="ml-auto shrink-0"
              disabled={generating.pr || generating.commit}
              onClick={() => {
                if (hasChanges && includeLocalChanges && !commitMessage.trim())
                  void generate('commit');
                void generate('pr', true);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              {generating.pr ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {generating.pr ? 'Generating…' : 'Regenerate'}
            </Button>
          </header>
          <div className="grid gap-5 p-6">
            {hasBranch ? (
              <div className="flex items-center gap-2 rounded-lg border border-hairline bg-background/55 px-3 py-2 text-muted-foreground text-xs">
                <GitBranch className="size-3.5 shrink-0" />
                <span className="min-w-0 truncate font-medium text-foreground/80">{branch}</span>
                <span className="ml-auto shrink-0">will be pushed automatically</span>
              </div>
            ) : (
              <label className="grid gap-2 text-sm" htmlFor="pull-request-branch-name">
                <span className="font-medium text-foreground/90">Branch name</span>
                <Input
                  className="h-11 rounded-lg px-3.5 placeholder:text-muted-foreground/60"
                  id="pull-request-branch-name"
                  onChange={(event) => setBranchName(event.target.value)}
                  placeholder="feature/my-change"
                  value={branchName}
                />
                {branchExists && <span className="text-destructive text-xs">Branch exists</span>}
                <span className="text-muted-foreground text-xs">
                  A new branch will be created from this worktree.
                </span>
              </label>
            )}
            <label className="grid gap-2 text-sm" htmlFor="pull-request-title">
              <span className="font-medium text-foreground/90">Title</span>
              <Input
                autoFocus
                className="h-11 rounded-lg px-3.5 placeholder:text-muted-foreground/60"
                id="pull-request-title"
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Summarize the change"
                value={title}
              />
            </label>
            <label className="grid gap-2 text-sm" htmlFor="pull-request-description">
              <span className="flex items-center justify-between gap-3">
                <span className="font-medium text-foreground/90">Description</span>
                <span className="text-muted-foreground text-xs">Markdown supported</span>
              </span>
              <Textarea
                className="min-h-36 resize-y rounded-lg px-3.5 py-3 leading-5 placeholder:text-muted-foreground/60"
                id="pull-request-description"
                onChange={(event) => setBody(event.target.value)}
                placeholder="What changed, and why?"
                value={body}
              />
            </label>
            <label
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-hairline bg-muted/20 p-3.5 transition-colors hover:bg-muted/35"
              htmlFor="pull-request-draft"
            >
              <Checkbox
                checked={draft}
                className="mt-0.5"
                id="pull-request-draft"
                onCheckedChange={(checked) => setDraft(checked === true)}
              />
              <span className="grid gap-0.5">
                <span className="font-medium text-sm">Create as draft</span>
                <span className="text-muted-foreground text-xs leading-5">
                  Open the pull request for early feedback before it is ready to merge.
                </span>
              </span>
            </label>
            {hasChanges && (
              <label
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-hairline bg-muted/20 p-3.5 transition-colors hover:bg-muted/35"
                htmlFor="pull-request-include-local-changes"
              >
                <Checkbox
                  checked={includeLocalChanges}
                  className="mt-0.5"
                  id="pull-request-include-local-changes"
                  onCheckedChange={(checked) => {
                    const include = checked === true;
                    setIncludeLocalChanges(include);
                    if (include && !commitMessage.trim()) void generate('commit');
                  }}
                />
                <span className="grid gap-0.5">
                  <span className="font-medium text-sm">Commit and push local changes</span>
                  <span className="text-muted-foreground text-xs leading-5">
                    Include the current worktree changes in this pull request.
                  </span>
                </span>
              </label>
            )}
            {hasChanges && includeLocalChanges && <CommitScopeChoice state={commitScope} />}
            {error !== null && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive text-sm">
                {error}
              </p>
            )}
          </div>
          <footer className="flex justify-end gap-2 px-6 py-4">
            <Button disabled={working} onClick={() => onClose(null)} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={
                working ||
                generating.pr ||
                !title.trim() ||
                !body.trim() ||
                (hasChanges &&
                  includeLocalChanges &&
                  (generating.commit || !commitMessage.trim())) ||
                (!hasBranch && (!branchName.trim() || branchExists))
              }
              onClick={() => void create()}
            >
              <GitPullRequest className="size-4" />
              {working ? 'Creating…' : draft ? 'Create draft PR' : 'Create PR'}
            </Button>
          </footer>
        </div>
      </DialogContent>
    </Dialog>
  );
}
